import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  TICKET_PRIORITY_LABELS,
  TICKET_STATUSES,
  TICKET_STATUS_LABELS,
  type TicketStatus,
} from '@sac/contracts'
import { api } from '../lib/api'
import { qk } from '../lib/query'
import { Icon } from '../components/icons'

type Filter = 'abertos' | TicketStatus

const FILTERS: [Filter, string][] = [
  ['abertos', 'Abertos'],
  ...TICKET_STATUSES.map((s) => [s, TICKET_STATUS_LABELS[s]] as [Filter, string]),
]

/**
 * The ticket queue.
 *
 * Defaults to everything unresolved rather than everything ever created — a queue that opens on a
 * pile of closed work is one nobody uses twice.
 */
export function Tickets() {
  const [filter, setFilter] = useState<Filter>('abertos')
  const qc = useQueryClient()

  const { data: tickets, isLoading } = useQuery({
    queryKey: qk.allTickets(filter),
    queryFn: () =>
      filter === 'abertos' ? api.tickets({ openOnly: true }) : api.tickets({ status: filter }),
  })

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; status: TicketStatus }) => api.updateTicket(input.id, { status: input.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  })

  const list = tickets ?? []

  return (
    <div className="tickets-page">
      <div className="tickets-head">
        <h1>
          <Icon name="ticket" size={18} />
          Tickets
        </h1>
        <div className="tickets-filters">
          {FILTERS.map(([f, label]) => (
            <button key={f} className={`chip ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Carregando…</div>
      ) : list.length === 0 ? (
        <div className="empty" style={{ padding: '40px 10px' }}>
          Nenhum ticket {filter === 'abertos' ? 'aberto' : TICKET_STATUS_LABELS[filter].toLowerCase()}.
          <br />
          Tickets são criados a partir de uma conversa, no painel do cliente.
        </div>
      ) : (
        <table className="tickets-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Assunto</th>
              <th>Cliente</th>
              <th>Pedido</th>
              <th>Responsável</th>
              <th>Prioridade</th>
              <th>Criado</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id} className={t.status === 'resolved' ? 'done' : ''}>
                <td className="tk-num" data-label="#">#{t.number}</td>
                <td data-label="Assunto">
                  <div className="tk-subj">{t.subject}</div>
                  {t.description && <div className="tk-desc">{t.description}</div>}
                </td>
                <td data-label="Cliente">{t.customerName}</td>
                <td data-label="Pedido">{t.orderNumber ? `#${t.orderNumber}` : '—'}</td>
                <td data-label="Responsável">{t.assignedAgentName ?? '—'}</td>
                <td data-label="Prioridade">
                  <span className={`pri ${t.priority}`}>{TICKET_PRIORITY_LABELS[t.priority]}</span>
                </td>
                <td data-label="Criado">{new Date(t.createdAt).toLocaleDateString('pt-BR')}</td>
                <td data-label="Status">
                  <select
                    className="tk-status"
                    value={t.status}
                    onChange={(e) => updateMutation.mutate({ id: t.id, status: e.target.value as TicketStatus })}
                  >
                    {TICKET_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {TICKET_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
